/**
 * StaffAttendanceLeavesTab — embedded inside StaffDetail's "Attendance &
 * Leaves" tab via render-prop injection (Prompt 11E.6), same pattern as
 * StudentBookingsTab/StudentBillingTab for StudentDetail.
 *
 * Only reachable via `/staff/:id`, which is ADMIN_ROLES-gated at the route
 * layer — so this is always the admin/owner/manager view of a staff
 * member's attendance + leaves, never self-view (that's MyLeavesPage).
 *
 * Calendar heat view + counts (available/availed/balance, attendance %
 * this month) + manual per-day mark + leave history + manual/backdated
 * leave entry form.
 */

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input, Dropdown, Badge } from '@/components/common';
import {
  useStaffAttendanceMonth,
  useMarkStaffAttendance,
  type StaffDayStatus,
} from '@features/attendance/api/staffAttendance';
import {
  useStaffLeaveBalance,
  useStaffLeaves,
  useManualLeaveEntry,
  useLeavesConfig,
  type LeaveType,
} from '@features/attendance/api/leaves';
import type { ApiError } from '@core/api/client';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatBox = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
`;

const HeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const HeatCell = styled.div<{ $status?: StaffDayStatus }>`
  aspect-ratio: 1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: ${({ $status }) => ($status ? '#fff' : '#94a3b8')};
  background: ${({ $status }) =>
    $status === 'present' ? '#16a34a' : $status === 'absent' ? '#dc2626' : '#e2e8f0'};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const HistoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const statusVariant = (status: string): 'primary' | 'success' | 'error' | 'default' =>
  status === 'pending' ? 'primary' : status === 'approved' ? 'success' : 'error';

interface Props {
  staffId: string;
}

export const StaffAttendanceLeavesTab: React.FC<Props> = ({ staffId }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based

  const { data: monthData } = useStaffAttendanceMonth(staffId, year, month);
  const { data: balance } = useStaffLeaveBalance(staffId, year);
  const { data: leaveHistory } = useStaffLeaves(staffId);
  const { data: config } = useLeavesConfig();
  const markAttendance = useMarkStaffAttendance(staffId, year, month);
  const manualLeaveEntry = useManualLeaveEntry(staffId);

  const [markDate, setMarkDate] = useState('');
  const [markStatus, setMarkStatus] = useState<StaffDayStatus>('present');

  const [leaveType, setLeaveType] = useState<LeaveType>('casual');
  const [leaveStart, setLeaveStart] = useState('');
  const [leaveEnd, setLeaveEnd] = useState('');
  const [leaveNote, setLeaveNote] = useState('');

  const daysByDate = useMemo(() => {
    const map = new Map(monthData?.days.map((d) => [d.date, d]));
    return map;
  }, [monthData]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=Sun

  const goPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const goNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const handleMark = async () => {
    if (!markDate) {
      toast.error('Pick a date to mark.');
      return;
    }
    try {
      await markAttendance.mutateAsync({ date: markDate, body: { status: markStatus } });
      toast.success('Attendance marked.');
      setMarkDate('');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not mark attendance.');
    }
  };

  const handleManualLeave = async () => {
    if (!leaveStart || !leaveEnd) {
      toast.error('Pick a start and end date.');
      return;
    }
    try {
      await manualLeaveEntry.mutateAsync({
        leave_type: leaveType,
        start_date: leaveStart,
        end_date: leaveEnd,
        note: leaveNote || undefined,
      });
      toast.success('Leave entry recorded (auto-approved).');
      setLeaveStart('');
      setLeaveEnd('');
      setLeaveNote('');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not record the leave entry.');
    }
  };

  const leaveTypeOptions = (config?.leave_types ?? ['casual', 'sick', 'other']).map((t) => ({
    value: t,
    label: t.charAt(0).toUpperCase() + t.slice(1),
  }));

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div data-testid="candeza-staff-attendance-leaves-tab">
      <StatsRow>
        <StatBox>
          <StatLabel>Available</StatLabel>
          <StatValue>{balance?.allocated ?? '—'}</StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Availed</StatLabel>
          <StatValue>{balance?.availed ?? '—'}</StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Balance</StatLabel>
          <StatValue>{balance?.balance ?? '—'}</StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Attendance % ({monthLabel})</StatLabel>
          <StatValue>
            {monthData?.attendance_pct != null ? `${monthData.attendance_pct}%` : '—'}
          </StatValue>
        </StatBox>
      </StatsRow>

      <Grid>
        <Card padding="lg">
          <CardHeader>
            <CardTitle>Attendance — {monthLabel}</CardTitle>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" size="sm" onClick={goPrevMonth}>
                ← Prev
              </Button>
              <Button variant="outline" size="sm" onClick={goNextMonth}>
                Next →
              </Button>
            </div>
          </CardHeader>

          <div style={{ marginTop: 16 }} data-testid="candeza-staff-attendance-heatmap">
            <HeatGrid>
              {Array.from({ length: firstWeekday }).map((_, i) => (
                <div key={`pad-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const entry = daysByDate.get(iso);
                return (
                  <HeatCell
                    key={iso}
                    $status={entry?.status}
                    title={entry ? `${iso}: ${entry.status} (${entry.source})` : iso}
                    data-testid={`candeza-staff-attendance-day-${iso}`}
                  >
                    {day}
                  </HeatCell>
                );
              })}
            </HeatGrid>
          </div>

          <CardHeader style={{ marginTop: 24 }}>
            <CardTitle>Manual mark</CardTitle>
          </CardHeader>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
            <Input
              label="Date"
              type="date"
              value={markDate}
              onChange={(e) => setMarkDate(e.target.value)}
              data-testid="candeza-staff-attendance-mark-date"
            />
            <Dropdown
              label="Status"
              options={[
                { value: 'present', label: 'Present' },
                { value: 'absent', label: 'Absent' },
              ]}
              value={markStatus}
              onChange={(v) => setMarkStatus(v as StaffDayStatus)}
              testId="candeza-staff-attendance-mark-status"
            />
            <Button
              onClick={handleMark}
              disabled={markAttendance.isPending}
              data-testid="candeza-staff-attendance-mark-submit"
            >
              Mark
            </Button>
          </div>
        </Card>

        <Card padding="lg">
          <CardHeader>
            <CardTitle>Add leave entry</CardTitle>
          </CardHeader>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Backdated/offline leave — auto-approved, audited to your account.
          </p>
          <FormGrid style={{ marginTop: 12 }}>
            <Dropdown
              label="Type"
              options={leaveTypeOptions}
              value={leaveType}
              onChange={(v) => setLeaveType(v as LeaveType)}
              testId="candeza-staff-leave-manual-type"
            />
            <div />
            <Input
              label="Start date"
              type="date"
              value={leaveStart}
              onChange={(e) => setLeaveStart(e.target.value)}
              data-testid="candeza-staff-leave-manual-start"
            />
            <Input
              label="End date"
              type="date"
              value={leaveEnd}
              onChange={(e) => setLeaveEnd(e.target.value)}
              data-testid="candeza-staff-leave-manual-end"
            />
          </FormGrid>
          <div style={{ marginTop: 12 }}>
            <Input
              label="Note"
              value={leaveNote}
              onChange={(e) => setLeaveNote(e.target.value)}
              data-testid="candeza-staff-leave-manual-note"
            />
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleManualLeave}
              disabled={manualLeaveEntry.isPending}
              data-testid="candeza-staff-leave-manual-submit"
            >
              Record entry
            </Button>
          </div>

          <CardHeader style={{ marginTop: 24 }}>
            <CardTitle>Leave history</CardTitle>
          </CardHeader>
          <div style={{ marginTop: 8 }} data-testid="candeza-staff-leave-history">
            {(leaveHistory?.items ?? []).length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 14 }}>No leave records yet.</p>
            ) : (
              leaveHistory?.items.map((leave) => (
                <HistoryRow key={leave.id}>
                  <div>
                    <strong style={{ textTransform: 'capitalize', fontSize: 13 }}>
                      {leave.leave_type}
                    </strong>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {leave.start_date} → {leave.end_date} ({leave.days}d)
                    </div>
                  </div>
                  <Badge variant={statusVariant(leave.status)} size="sm">
                    {leave.status}
                  </Badge>
                </HistoryRow>
              ))
            )}
          </div>
        </Card>
      </Grid>
    </div>
  );
};

export default StaffAttendanceLeavesTab;
